import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
} from "@mui/material";
import { useState } from "react";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { createAuthUser } from "../firebase/user";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CreateUserDialog({ open, onClose }: Props) {
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleClose = () => {
    setEmail("");
    setPassword("");
    setIsAdmin(false);
    setError("");
    setSuccess("");
    onClose();
  };

  const createUser = async () => {
    if (!email || !password) {
      setError("El correo y la contraseña son obligatorios.");
      return;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // 1. Crear usuario en Firebase Auth (sin cerrar sesión del admin)
      const uid = await createAuthUser(email.trim(), password);

      // 2. Guardar perfil en Firestore colección "users"
      await setDoc(doc(db, "users", uid), {
        uid,
        email: email.trim(),
        isAdmin,
        role: isAdmin ? "admin" : "client",
        projects: [],
        createdAt: serverTimestamp(),
      });

      setSuccess(`✅ Usuario "${email}" creado correctamente.`);
      setEmail("");
      setPassword("");
      setIsAdmin(false);

      // Cerrar automáticamente tras 1.5s
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error("Error creando usuario:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("Ya existe un usuario registrado con ese correo electrónico.");
      } else if (err.code === "auth/invalid-email") {
        setError("El formato del correo electrónico no es válido.");
      } else {
        setError("Error al crear el usuario: " + (err.message || err.code));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>Crear usuario de portal</DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            disabled={loading}
          />

          <TextField
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            disabled={loading}
            helperText="Mínimo 6 caracteres"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                disabled={loading}
              />
            }
            label="Administrador"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={createUser} disabled={loading}>
          {loading ? "Creando..." : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
